/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useRef } from 'react';

import { Box, Button, Icon, InputBase } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { OdProtosWebData_RecordedFeature as RecordedFeature } from '__generated__/globalTypes';
import { MarginProps } from '@opendoor/bricks/system/theme';

interface IVideoUploadButtonProps {
  id: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  loading?: boolean;
  disabled?: boolean;
  actionText?: string;
  multiple?: boolean;
  variant?: 'primary' | 'secondary' | 'link' | undefined;
  withPlusIcon?: boolean;
  recordedFeature: RecordedFeature;
}

// NOTE: See accessibility safe hiding of content
// https://www.a11yproject.com/posts/2013-01-11-how-to-hide-content/
const visuallyHide = css`
  border: 0;
  clip: rect(1px, 1px, 1px, 1px);
  height: 1px;
  overflow: hidden;
  padding: 0;
  position: absolute !important;
  white-space: nowrap;
  width: 1px;
`;

// component-test-check-ignore
/** Button for taking or uploading videos */
export const VideoUploadButton: React.FC<IVideoUploadButtonProps & MarginProps> = ({
  loading,
  disabled,
  actionText,
  multiple,
  variant,
  withPlusIcon,
  recordedFeature,
  id,
  onChange,
  ...marginProps
}) => {
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const { isMobile } = useDevice();
  const metricsSuffix =
    (isMobile ? 'mobile-' : 'desktop-') +
    (actionText ? actionText.toLowerCase().replace(/\s/g, '-') : '') +
    '-' +
    recordedFeature.toLowerCase();

  const acceptTypes = 'video/mp4,video/quicktime,video/mov';
  const onClick = () => {
    if (hiddenInputRef?.current) {
      /* Allows selecting the same file to trigger onChnage
         useful for re-selecting the same file to upload
         when an error has occured.
      */
      hiddenInputRef.current.value = '';
    }
    hiddenInputRef?.current && hiddenInputRef.current.click();
  };
  return (
    <Box {...marginProps}>
      <InputBase
        ref={hiddenInputRef}
        type="file"
        css={visuallyHide}
        accept={acceptTypes}
        id={id}
        onChange={onChange}
        multiple={multiple}
      />
      <Button
        aria-label={`Upload video for ${metricsSuffix}`}
        analyticsName={`cake-${metricsSuffix}`}
        disabled={disabled || loading}
        loading={loading}
        onClick={onClick}
        variant={variant}
        p={variant === 'link' ? 0 : 'auto'}
      >
        {withPlusIcon && <Icon name="plus" size={12} color="brand50" />}
        {actionText}
      </Button>
    </Box>
  );
};
